import { fetchAdminUserLandingLinks } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingLink } from '@/entities/landing.entity';
import { formatMetaPixelOptionLabel } from '@/entities/landing.entity';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { hostnameFromLandingUrl } from '@/pages/landings/lib/landing-url';
import { CopyIcon, ExternalLinkIcon, LinkIcon, LoaderCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

type FetchLinksFn = (
	profileId: number,
	params?: { siteId?: number; cursor?: string; limit?: number },
) => Promise<{ items: ILandingLink[]; nextCursor?: string | null }>;

interface LandingsLinksSubtabProps {
	profileId: number;
	fetchLinks?: FetchLinksFn;
}

export default function LandingsLinksSubtab({
	profileId,
	fetchLinks = fetchAdminUserLandingLinks,
}: LandingsLinksSubtabProps) {
	const [links, setLinks] = useState<ILandingLink[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const fetchingRef = useRef(false);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	const loadInitial = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const data = await fetchLinks(profileId, { limit: 50 });
			setLinks(data.items);
			setNextCursor(data.nextCursor ?? null);
			setHasMore(data.nextCursor != null);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [profileId, fetchLinks]);

	useEffect(() => {
		void loadInitial();
	}, [loadInitial]);

	const loadMore = useCallback(async () => {
		if (fetchingRef.current || !hasMore || !nextCursor) return;
		fetchingRef.current = true;
		setIsFetching(true);
		try {
			const data = await fetchLinks(profileId, { limit: 50, cursor: nextCursor });
			setLinks(prev => [...prev, ...data.items]);
			setNextCursor(data.nextCursor ?? null);
			setHasMore(data.nextCursor != null);
		} catch {
			setError(true);
		} finally {
			fetchingRef.current = false;
			setIsFetching(false);
		}
	}, [profileId, hasMore, nextCursor]);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasMore) return;

		const observer = new IntersectionObserver(entries => {
			if (entries[0]?.isIntersecting) {
				void loadMore();
			}
		});
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasMore, loadMore]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error && links.length === 0) {
		return (
			<LandingsErrorState
				message='Лендинги недоступны. Проверьте сервис landing и LANDING_INTERNAL_SECRET.'
				onRetry={() => void loadInitial()}
			/>
		);
	}

	if (links.length === 0) {
		return (
			<LandingsEmptyState
				icon={LinkIcon}
				title='Ссылок нет'
				description='Пользователь ещё не создал трекинговые ссылки.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-3'>
			{links.map(link => (
				<AdminLinkRow key={link.id} link={link} />
			))}
			<div ref={sentinelRef} className='h-1' />
			{isFetching ? (
				<div className='flex justify-center py-2'>
					<LoaderCircleIcon className='animate-spin text-muted-foreground' size={20} />
				</div>
			) : null}
		</div>
	);
}

function AdminLinkRow({ link }: { link: ILandingLink }) {
	const hostname = hostnameFromLandingUrl(link.url);

	function copyUrl() {
		navigator.clipboard
			.writeText(link.url)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<div className='rounded-xl border border-border bg-card p-3 shadow-sm'>
			<div className='flex items-start justify-between gap-2'>
				<div className='min-w-0 flex-1'>
					<p className='font-medium truncate'>{hostname}</p>
					<p className='text-xs text-muted-foreground mt-0.5 font-mono'>{link.id}</p>
					<div className='mt-1.5 flex flex-wrap items-center gap-2'>
						<span className='inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
							{link.templateSlug}
						</span>
						{link.metaPixel ? (
							<span className='inline-flex items-center rounded-md bg-chart-4/15 px-2 py-0.5 text-xs text-chart-4'>
								{formatMetaPixelOptionLabel({
									name: link.metaPixel.name,
									conversionEvent: link.metaPixel.conversionEvent,
								})}
							</span>
						) : null}
						{link.buildId ? (
							<span className='text-xs text-muted-foreground truncate max-w-[140px]' title={link.buildId}>
								{link.buildId}
							</span>
						) : null}
					</div>
				</div>
				<div className='flex gap-1 shrink-0'>
					<Button type='button' variant='ghost' size='icon' className='size-8' onClick={copyUrl}>
						<CopyIcon size={14} />
					</Button>
					<Button type='button' variant='ghost' size='icon' className='size-8' asChild>
						<a href={link.url} target='_blank' rel='noopener noreferrer'>
							<ExternalLinkIcon size={14} />
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}
